import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import createPersistedState from 'vuex-persistedstate'
import api from '../service/api'
import msalInstance from '../auth/msalInstance'
import { loginRequest } from '../auth/msalConfig'

Vue.use(Vuex)

export default new Vuex.Store({
  plugins: [createPersistedState({ paths: ['selectedRegatta', 'userRole', 'userName'] })],

  state: {
    // Auth
    account: null,
    idToken: null,
    userRole: null,
    userName: null,

    // Rowing data
    selectedRegatta: null,
    regattas: [],
    events: [],
    races: [],
    participantResults: [],
    cachedResultIds: [],
    allAthleteResults: [],
    athleteResultsLoading: false,
    pinnedRegattaIds: [],
    pinnedRegattas: [],
    loading: false,
    resultsLoading: false,
    notification: null,
    authError: null
  },

  getters: {
    // Auth
    isAuthenticated: (state) => !!state.account,
    idToken: (state) => state.idToken,
    userRole: (state) => state.userRole,
    userName: (state) => state.userName,
    isAdmin: (state) => state.userRole === 'admin',
    getSelectedRegattaId: (state) => state.selectedRegatta?.regattaId || null,
    getSelectedRegattaName: (state) => state.selectedRegatta?.name || null,

    // Rowing
    racesForEvent: (state) => (eventCode) => {
      if (!eventCode) return state.races
      return state.races.filter(r => r.eventCode === eventCode)
    },

    resultsByEvent: (state) => (eventCode) => {
      const rows = eventCode
        ? state.participantResults.filter(r => r.eventCode === eventCode)
        : state.participantResults
      const crews = {}
      rows.forEach(r => {
        const key = `${r.raceNumber}__${r.crewTitle}`
        if (!crews[key]) {
          crews[key] = {
            raceNumber: r.raceNumber,
            eventCode: r.eventCode,
            crewTitle: r.crewTitle,
            raceLane: r.raceLane,
            resultPlace: r.resultPlace,
            resultFinish: r.resultFinish,
            raceStatus: r.raceStatus,
            athletes: []
          }
        }
        crews[key].athletes.push({ name: r.athleteName, club: r.athleteClub })
      })
      return Object.values(crews).sort((a, b) => {
        const pa = parsePlaceNum(a.resultPlace)
        const pb = parsePlaceNum(b.resultPlace)
        if (pa !== pb) return pa - pb
        return (a.raceNumber || '').localeCompare(b.raceNumber || '')
      })
    },

    laneStats: (state) => {
      if (state.participantResults.length) {
        const stats = {}
        state.participantResults.forEach(r => {
          const lane = r.raceLane
          const place = parsePlaceNum(r.resultPlace)
          if (!lane || !place) return
          if (!stats[lane]) stats[lane] = { lane, races: 0, wins: 0, podiums: 0, totalPlace: 0 }
          stats[lane].races++
          stats[lane].totalPlace += place
          if (place === 1) stats[lane].wins++
          if (place <= 3) stats[lane].podiums++
        })
        return Object.values(stats)
          .map(s => ({
            ...s,
            winPct: s.races ? ((s.wins / s.races) * 100).toFixed(1) : 0,
            podiumPct: s.races ? ((s.podiums / s.races) * 100).toFixed(1) : 0,
            avgPlace: s.races ? (s.totalPlace / s.races).toFixed(2) : 0
          }))
          .sort((a, b) => a.lane - b.lane)
      }
      const stats = {}
      state.races.forEach(race => {
        race.lanes.forEach(lane => {
          if (!lane.laneNum || !lane.place) return
          if (!stats[lane.laneNum]) stats[lane.laneNum] = { lane: lane.laneNum, races: 0, wins: 0, podiums: 0, totalPlace: 0 }
          stats[lane.laneNum].races++
          stats[lane.laneNum].totalPlace += lane.place
          if (lane.place === 1) stats[lane.laneNum].wins++
          if (lane.place <= 3) stats[lane.laneNum].podiums++
        })
      })
      return Object.values(stats)
        .map(s => ({
          ...s,
          winPct: s.races ? ((s.wins / s.races) * 100).toFixed(1) : 0,
          podiumPct: s.races ? ((s.podiums / s.races) * 100).toFixed(1) : 0,
          avgPlace: s.races ? (s.totalPlace / s.races).toFixed(2) : 0
        }))
        .sort((a, b) => a.lane - b.lane)
    }
  },

  mutations: {
    // Auth
    SET_ACCOUNT(state, { account, idToken }) {
      state.account = account
      state.idToken = idToken
    },
    SET_ID_TOKEN(state, token) { state.idToken = token },
    SET_USER_PROFILE(state, { role, displayName }) {
      state.userRole = role
      state.userName = displayName
    },
    CLEAR_AUTH(state) {
      state.account = null
      state.idToken = null
      state.userRole = null
      state.userName = null
    },

    // Rowing
    SET_SELECTED_REGATTA(state, regatta) { state.selectedRegatta = regatta },
    SET_REGATTAS(state, list) { state.regattas = list },
    SET_EVENTS(state, list) { state.events = list },
    SET_RACES(state, list) { state.races = list },
    SET_PARTICIPANT_RESULTS(state, list) { state.participantResults = list },
    SET_CACHED_RESULT_IDS(state, list) { state.cachedResultIds = list },
    SET_ALL_ATHLETE_RESULTS(state, list) { state.allAthleteResults = list },
    SET_ATHLETE_RESULTS_LOADING(state, val) { state.athleteResultsLoading = val },
    SET_PINNED_REGATTAS(state, ids) { state.pinnedRegattaIds = ids },
    SET_PINNED_REGATTAS_DETAIL(state, list) { state.pinnedRegattas = list },
    SET_LOADING(state, val) { state.loading = val },
    SET_RESULTS_LOADING(state, val) { state.resultsLoading = val },
    SET_NOTIFICATION(state, n) { state.notification = n },
    SET_AUTH_ERROR(state, msg) { state.authError = msg }
  },

  actions: {
    // ── Auth ──────────────────────────────────────────────────────────────────

    async loginSuccess({ commit, dispatch, state }, authResult) {
      commit('SET_AUTH_ERROR', null)
      commit('SET_ACCOUNT', { account: authResult.account, idToken: authResult.idToken })
      // Skip profile fetch if persisted profile is already in the store (page reload with silent token)
      if (!state.userName || !state.userRole) {
        await dispatch('fetchUserProfile', authResult.idToken)
      }
    },

    async fetchUserProfile({ commit }, idToken) {
      try {
        const { data } = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${idToken}` }
        })
        commit('SET_USER_PROFILE', { role: data.role, displayName: data.display_name })
      } catch (error) {
        const status = error?.response?.status
        const msg = error?.response?.data?.error || `Auth failed (HTTP ${status})`
        console.error('fetchUserProfile failed:', status, error?.response?.data)
        if (status === 401 || status === 403) {
          commit('SET_AUTH_ERROR', msg)
          commit('CLEAR_AUTH')
        } else {
          // Databricks unavailable — stay logged in with minimal role
          commit('SET_USER_PROFILE', { role: 'viewer', displayName: '' })
        }
      }
    },

    async initiateLogin() {
      // Clear any stale MSAL interaction state that would block loginRedirect
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('msal.') || k.includes('interaction.status'))
        .forEach(k => sessionStorage.removeItem(k))
      await msalInstance.loginRedirect(loginRequest)
    },

    logOut({ commit }) {
      const account = msalInstance.getAllAccounts()[0]
      commit('CLEAR_AUTH')
      msalInstance.logoutRedirect({ account })
    },

    // ── Rowing ────────────────────────────────────────────────────────────────

    async fetchRegattas({ commit }, params = { state: 'WA' }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.getRegattas(params)
        commit('SET_REGATTAS', Array.isArray(data) ? data : [])
      } catch (e) {
        commit('SET_NOTIFICATION', { text: 'Failed to load regattas', color: 'error', icon: 'mdi-alert-circle' })
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchEvents({ commit }, regattaId) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.getEvents(regattaId)
        commit('SET_EVENTS', Array.isArray(data) ? data : [])
      } catch (e) {
        commit('SET_EVENTS', [])
        if (e.response?.status !== 404) {
          commit('SET_NOTIFICATION', { text: 'Failed to load events', color: 'error', icon: 'mdi-alert-circle' })
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchRaces({ commit }, { regattaId, params = {} }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.getRaces(regattaId, params)
        commit('SET_RACES', Array.isArray(data) ? data : [])
      } catch (e) {
        commit('SET_RACES', [])
        if (e.response?.status !== 404) {
          commit('SET_NOTIFICATION', { text: 'Failed to load races', color: 'error', icon: 'mdi-alert-circle' })
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchParticipantResults({ commit }, regattaId) {
      commit('SET_RESULTS_LOADING', true)
      commit('SET_PARTICIPANT_RESULTS', [])
      try {
        const { data } = await api.getResults(regattaId)
        commit('SET_PARTICIPANT_RESULTS', Array.isArray(data) ? data : [])
      } catch (e) {
        const msg = e.response?.status === 409
          ? 'Participant results only available after hours (after 22:00 AEDT)'
          : 'Failed to load participant results'
        commit('SET_NOTIFICATION', { text: msg, color: 'warning', icon: 'mdi-clock-outline' })
      } finally {
        commit('SET_RESULTS_LOADING', false)
      }
    },

    async fetchAllCachedResults({ commit }) {
      commit('SET_ATHLETE_RESULTS_LOADING', true)
      try {
        const thisYear = new Date().getFullYear()
        const cutoff = new Date()
        cutoff.setFullYear(cutoff.getFullYear() - 1)

        const [cacheRes, waThis, waLast] = await Promise.allSettled([
          api.getCacheList(),
          api.getRegattas({ state: 'WA' }),
          api.getRegattas({ state: 'WA', year: thisYear - 1 })
        ])

        const cached = cacheRes.status === 'fulfilled' ? cacheRes.value.data : []
        const regattaLookup = {}
        for (const res of [waThis, waLast]) {
          if (res.status === 'fulfilled' && Array.isArray(res.value.data)) {
            res.value.data.forEach(r => { regattaLookup[r.regattaId] = r.name })
          }
        }

        // Pre-fetch names for any regattas not covered by the WA list (parallel batch)
        const missingIds = cached.map(c => c.regattaId).filter(id => !regattaLookup[id])
        await Promise.all(
          missingIds.map(async id => {
            try {
              const { data: r } = await api.getRegatta(id)
              regattaLookup[id] = r?.name || `Regatta ${id}`
            } catch {
              regattaLookup[id] = `Regatta ${id}`
            }
          })
        )

        const results = await Promise.all(
          cached.map(async ({ regattaId, fetchedAt }) => {
            try {
              const { data } = await api.getCachedResults(regattaId)
              const rows = Array.isArray(data) ? data : []
              if (!rows.length) return []

              const regattaName = regattaLookup[regattaId]
              const regattaDate = rows[0]?.raceTime ? rows[0].raceTime.split(' ')[0]
                : fetchedAt ? fetchedAt.split(' ')[0] : null
              if (!regattaDate || new Date(regattaDate) < cutoff) return []

              return rows.map(r => ({ ...r, regattaName, regattaDate }))
            } catch (e) { return [] }
          })
        )
        commit('SET_ALL_ATHLETE_RESULTS', results.flat())
      } catch (e) {
        // silent — table will show empty
      } finally {
        commit('SET_ATHLETE_RESULTS_LOADING', false)
      }
    },

    selectRegatta({ commit, dispatch }, regatta) {
      commit('SET_SELECTED_REGATTA', regatta)
      commit('SET_EVENTS', [])
      commit('SET_RACES', [])
      commit('SET_PARTICIPANT_RESULTS', [])
      if (regatta) {
        dispatch('fetchEvents', regatta.regattaId)
        dispatch('fetchRaces', { regattaId: regatta.regattaId, params: { lanedraw: 'true' } })
      }
    },

    async fetchPinnedRegattas({ commit }) {
      try {
        const { data } = await api.getPinnedRegattas()
        const ids = Array.isArray(data) ? data : []
        commit('SET_PINNED_REGATTAS', ids)
        const details = await Promise.all(
          ids.map(async id => {
            try { return (await api.getRegatta(id)).data } catch { return null }
          })
        )
        commit('SET_PINNED_REGATTAS_DETAIL', details.filter(Boolean))
      } catch (e) {
        // silent
      }
    },

    async togglePinnedRegatta({ state, commit }, { regattaId, regatta }) {
      const current = state.pinnedRegattaIds
      const isPinning = !current.includes(regattaId)
      const updated = isPinning
        ? [...current, regattaId]
        : current.filter(id => id !== regattaId)
      try {
        await api.setPinnedRegattas(updated)
        commit('SET_PINNED_REGATTAS', updated)
        if (isPinning && regatta) {
          commit('SET_PINNED_REGATTAS_DETAIL', [...state.pinnedRegattas, regatta])
        } else {
          commit('SET_PINNED_REGATTAS_DETAIL', state.pinnedRegattas.filter(r => r.regattaId !== regattaId))
        }
      } catch (e) {
        commit('SET_NOTIFICATION', { text: 'Failed to update pinned regattas', color: 'error', icon: 'mdi-alert-circle' })
      }
    }
  }
})

function parsePlaceNum(place) {
  if (!place || place === '-') return null
  return parseInt(place)
}
